import { AxiosResponse } from 'axios'
import { UserManager, UserManagerSettings } from 'oidc-client-ts'
import { useCallback, useEffect, useState } from 'react'
import { codeResponse, getOidcClient, OpenIDContext } from '../../api/openid'
import learnBackendRequestHandler from '../../api/learnBackendRequestHandler'
import { createUrl } from '../../shared/UrlUtils/urlUtils'

const OpenID = ({ children }: DefaultProps) => {
	const subscriptionCheckout = new URLSearchParams(window.location.search).get(
		'subscription-checkout',
	)
	const code = new URLSearchParams(window.location.search).get('code')
	const SILENT_AUTH_TIMEOUT_MS = 8000
	const SILENT_RENEW_TIMEOUT_SECONDS = 10
	const ACCESS_TOKEN_EXPIRY_NOTIFICATION_TIMEOUT_IN_SECONDS = 60
	const [isProcessingSilentAuth, setIsProcessingSilentAuth] = useState(false)
	const [isGlobalAlertShown, setIsGlobalAlertShown] = useState(false)
	const [globalAlertMessage, setGlobalAlertMessage] = useState('')
	const [globalAlertSeverity, setGlobalAlertSeverity] = useState('error')
	const [profile, setProfile] = useState({
		authenticated: null as boolean | null,
		profile: {
			firstName: '',
			lastName: '',
			username: '',
			email: '',
			guid: '',
			image: '',
			isSubscribed: false,
			isTrialAvailable: false,
			subscriptionType: '',
			restrictions: {
				hasCSEAccess: false,
				hasTopicAccess: false,
				hasDrSecDimAccess: false,
			},
		},
	})

	const processUserInfoResponse = (
		userInfoResponse: AxiosResponse<UserInfoResponse>,
	) => ({
		authenticated: true,
		profile: {
			guid: userInfoResponse.data.guid,
			username: userInfoResponse.data.username,
			firstName: userInfoResponse.data.first_name,
			lastName: userInfoResponse.data.last_name,
			email: userInfoResponse.data.email,
			image: userInfoResponse.data.image ?? '',
			isSubscribed: userInfoResponse.data.subscription,
			isTrialAvailable: Boolean(userInfoResponse.data.is_trial_available),
			subscriptionType: userInfoResponse.data.subscription_type,
			restrictions: {
				hasCSEAccess: Boolean(userInfoResponse.data.restrictions.has_cse_access),
				hasTopicAccess: Boolean(
					userInfoResponse.data.restrictions.has_topic_access,
				),
				hasDrSecDimAccess: Boolean(
					userInfoResponse.data.restrictions.has_drsecdim_access,
				),
			},
		},
	})

	const processSessionStorageItems = useCallback(() => {
		// Handle redirects first - if there's a redirect URI stored, we need to go there
		if (sessionStorage.getItem('redirectURI')) {
			// If we're coming from a subscription checkout, save that information
			if (subscriptionCheckout) {
				sessionStorage.setItem('subscriptionCheckout', subscriptionCheckout)

				// Get the redirect URI and clear it from storage
				const redirectUrl = sessionStorage.getItem('redirectURI')!
				sessionStorage.removeItem('redirectURI')
				window.location.href = redirectUrl
			}
		}
	}, [subscriptionCheckout])

	const fetchUserInfoAndUpdateProfile = useCallback(async () => {
		learnBackendRequestHandler('userInfo').then(
			(userInfoResponse: AxiosResponse<UserInfoResponse>) => {
				if (userInfoResponse.data.username) {
					const profileData = processUserInfoResponse(userInfoResponse)
					processSessionStorageItems()
					setProfile(profileData)
				}
			},
		)
	}, [processSessionStorageItems])

	const heartbeat = useCallback(async () => {
		const client = await getOidcClient()

		if (code) {
			codeResponse(window.location.href)
				.then(resp => {
					const idToken = resp.id_token // User's 'passport'
					const newURL = createUrl(window.location.origin, {
						path: window.location.pathname,
					})
					learnBackendRequestHandler('login', undefined, idToken)
						.then(response => {
							if ((response.data as any).success) {
								processSessionStorageItems()
								window.location.href = window.location.origin
							} else {
								window.history.pushState('object', document.title, newURL)
							}
						})
						.catch(() => {
							window.history.pushState('object', document.title, newURL)
						})
				})
				.catch(() => {
					// Code exchange failed (likely already used), just check login state
					learnBackendRequestHandler('heartBeat').then(
						async silentLoginLearnUserInfo => {
							setIsProcessingSilentAuth(false)
							if (silentLoginLearnUserInfo.data.is_logged_in) {
								await fetchUserInfoAndUpdateProfile()
							}
						},
					)
				})
		}

		if (profile.authenticated === null) {
			learnBackendRequestHandler('heartBeat').then(async hbResp1 => {
				if ((hbResp1.data as any).is_logged_in) {
					await fetchUserInfoAndUpdateProfile()
				} else if (
					!hbResp1.data.is_logged_in &&
					!code &&
					!profile.authenticated &&
					!isProcessingSilentAuth
				) {
					// User not logged in, attempt silent login
					setIsProcessingSilentAuth(true)
					setProfile(prevState => ({
						...prevState,
						authenticated: false,
					}))

					// Set up postMessage listener for silent authentication
					let messageHandled = false
					const processedMessages = new Set()

					const handleSilentAuthMessage = (event: MessageEvent) => {
						if (
							event.origin !== window.location.origin ||
							event.data?.type === 'webpackOk' ||
							event.data?.type === 'webpackClose'
						) {
							return
						}

						// Prevent duplicate processing of the same message
						const messageKey = `${event.data?.type}-${event.data?.state || event.data?.code}`
						if (processedMessages.has(messageKey)) {
							return
						}
						processedMessages.add(messageKey)

						if (event.data?.type === 'oidc-silent-error') {
							if (messageHandled) return
							messageHandled = true
							setIsProcessingSilentAuth(false)

							window.removeEventListener('message', handleSilentAuthMessage)

							// Check actual login state after silent authentication error
							learnBackendRequestHandler('heartBeat').then(
								async silentLoginLearnUserInfo => {
									if (silentLoginLearnUserInfo.data.is_logged_in) {
										await fetchUserInfoAndUpdateProfile()
									}
								},
							)
						} else if (event.data?.type === 'oidc-silent-success') {
							if (messageHandled) return
							messageHandled = true

							window.removeEventListener('message', handleSilentAuthMessage)

							// Handle the authorization code exchange
							if (event.data.code) {
								// Try the code exchange, but handle state errors gracefully
								codeResponse(event.data.url)
									.then(resp => {
										const idToken = resp.id_token // User's 'passport'
										learnBackendRequestHandler('login', undefined, idToken)
											.then(response => {
												if (response.status === 200) {
													// Clear OIDC storage since we use cookies
													try {
														// Clear all OIDC-related storage items
														Object.keys(localStorage).forEach(key => {
															if (key.startsWith('oidc.')) {
																localStorage.removeItem(key)
															}
														})
														// Also clear any session storage items
														Object.keys(sessionStorage).forEach(key => {
															if (key.startsWith('oidc.')) {
																sessionStorage.removeItem(key)
															}
														})
													} catch {
														// Ignore storage errors
													}

													// Refetch user info after successful session creation
													learnBackendRequestHandler('heartBeat').then(
														async silentLoginLearnUserInfo => {
															setIsProcessingSilentAuth(false)
															if (silentLoginLearnUserInfo.data.is_logged_in) {
																await fetchUserInfoAndUpdateProfile()
															}
														},
													)
												}
											})
											.catch(() => {
												// Silent authentication error - ignore
											})
									})
									.catch(() => {
										// Code exchange failed (likely already used), just check login state
										learnBackendRequestHandler('heartBeat').then(
											async silentLoginLearnUserInfo => {
												setIsProcessingSilentAuth(false)
												if (silentLoginLearnUserInfo.data.is_logged_in) {
													await fetchUserInfoAndUpdateProfile()
												}
											},
										)
									})
							}
						}
					}

					window.addEventListener('message', handleSilentAuthMessage)

					const metadata = await client.metadataService.getMetadata()
					const userManagerSettings: UserManagerSettings = {
						authority: createUrl(import.meta.env.VITE_SECDIM_ID_BACKEND!, {
							path: 'openid',
							removeTrailingSlash: true,
						}),
						metadata,
						client_id: client.settings.client_id,
						redirect_uri: window.location.origin,
						post_logout_redirect_uri: createUrl(
							import.meta.env.VITE_SECDIM_ID_FRONTEND!,
							{ path: ['account', 'logout'], removeTrailingSlash: true },
						),
						response_type: 'code',
						scope: 'openid email profile',
						filterProtocolClaims: true,
						loadUserInfo: false,
						silent_redirect_uri: createUrl(window.location.origin, {
							path: 'silent-callback.html',
							removeTrailingSlash: true,
						}),
						silentRequestTimeoutInSeconds: SILENT_RENEW_TIMEOUT_SECONDS,
						automaticSilentRenew: false,
						includeIdTokenInSilentRenew: false,
						response_mode: 'query',
						monitorSession: false,
						revokeTokensOnSignout: true,
						accessTokenExpiringNotificationTimeInSeconds:
							ACCESS_TOKEN_EXPIRY_NOTIFICATION_TIMEOUT_IN_SECONDS,
					}

					const userManager: UserManager = new UserManager(userManagerSettings)

					// Set up cleanup timeout
					const cleanupTimeout = setTimeout(() => {
						if (!messageHandled) {
							setIsProcessingSilentAuth(false)
							window.removeEventListener('message', handleSilentAuthMessage)
						}
					}, SILENT_AUTH_TIMEOUT_MS)

					// Attempt silent authentication
					userManager
						.signinSilent()
						.then(() => {
							if (!messageHandled) {
								clearTimeout(cleanupTimeout)
								setIsProcessingSilentAuth(false)
								window.removeEventListener('message', handleSilentAuthMessage)
								learnBackendRequestHandler('heartBeat').then(
									async silentLoginLearnUserInfo => {
										if (silentLoginLearnUserInfo.data.is_logged_in) {
											await fetchUserInfoAndUpdateProfile()
										}
									},
								)
							}
						})
						.catch(() => {
							if (!messageHandled) {
								clearTimeout(cleanupTimeout)
								setIsProcessingSilentAuth(false)
								window.removeEventListener('message', handleSilentAuthMessage)

								learnBackendRequestHandler('heartBeat').then(
									async silentLoginLearnUserInfo => {
										if (silentLoginLearnUserInfo.data.is_logged_in) {
											await fetchUserInfoAndUpdateProfile()
										}
									},
								)
							}
						})
				} else {
					// User not logged in and no silent login needed
					setIsProcessingSilentAuth(false)
					setProfile(prevState => ({
						...prevState,
						authenticated: false,
					}))
				}
			})
		}
	}, [
		code,
		isProcessingSilentAuth,
		processSessionStorageItems,
		profile.authenticated,
		fetchUserInfoAndUpdateProfile,
	])

	useEffect(() => {
		heartbeat()
	}, [heartbeat])

	return (
		<OpenIDContext.Provider
			// eslint-disable-next-line react/jsx-no-constructed-context-values
			value={{
				authenticated: profile.authenticated,
				profile: profile.profile,
				refresh: heartbeat,
				isGlobalAlertShown,
				globalAlertMessage,
				globalAlertSeverity,
				setIsGlobalAlertShown,
				setGlobalAlertMessage,
				setGlobalAlertSeverity,
			}}
		>
			{children}
		</OpenIDContext.Provider>
	)
}

export default OpenID
